import React, { useState, useEffect } from 'react';
import { Activity } from '@/types/lead';
import { getActivityTypeDisplay } from '@/services/leads/activityService';
import { getLeadTimeline } from '@/services/leads/activityAggregatedService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageSquare, RefreshCw, CheckSquare, FileText, Phone, Mail, Calendar, MoreHorizontal } from 'lucide-react';
import { format } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface ActivityTimelineProps {
  leadId: string;
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ leadId }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  
  useEffect(() => {
    const fetchActivities = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Single aggregated query for all timeline data
        const timelineData = await getLeadTimeline(leadId);
        
        // Map status history to activities
        const statusActivities: Activity[] = timelineData.statusHistory.map(status => ({
          id: status.id,
          leadId,
          type: 'status_change',
          title: `Status changed to ${status.status}`,
          description: status.notes,
          performedBy: status.createdBy,
          createdAt: new Date(status.createdAt),
        }));
        
        // Map completed tasks to activities
        const taskActivities: Activity[] = timelineData.tasks
          .filter(task => task.status === 'completed')
          .map(task => ({
            id: task.id,
            leadId,
            type: 'task',
            title: `Task completed: ${task.title}`,
            description: task.description,
            performedBy: task.assignedTo,
            createdAt: new Date(task.completedAt || task.createdAt),
            metadata: { taskId: task.id },
          }));

        // Map email logs to activities
        const emailActivities: Activity[] = timelineData.emailLogs.map(log => ({
          id: log.id,
          leadId,
          type: 'email',
          title: `Email sent: ${log.subject}`,
          description: `To: ${log.toEmail}\nStatus: ${log.status}${log.errorMessage ? `\nError: ${log.errorMessage}` : ''}`,
          performedBy: log.sentBy,
          createdAt: new Date(log.sentAt),
          metadata: { 
            emailId: log.id,
            resendEmailId: log.resendEmailId,
            fromEmail: log.fromEmail,
            toEmail: log.toEmail,
            subject: log.subject,
            status: log.status,
          },
        }));
        
        // Combine and sort all activities
        const allActivities = [
          ...timelineData.activities, 
          ...statusActivities, 
          ...taskActivities, 
          ...emailActivities
        ];
        allActivities.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        
        setActivities(allActivities);
      } catch (err) {
        console.error('Error fetching activities:', err);
        setError('Failed to load activity timeline');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchActivities();
  }, [leadId]);
  
  const getActivityIcon = (type: string) => {
    const icons = {
      note: <MessageSquare className="h-5 w-5" />,
      status_change: <RefreshCw className="h-5 w-5" />,
      task: <CheckSquare className="h-5 w-5" />,
      document: <FileText className="h-5 w-5" />,
      call: <Phone className="h-5 w-5" />,
      email: <Mail className="h-5 w-5" />,
      meeting: <Calendar className="h-5 w-5" />,
      other: <MoreHorizontal className="h-5 w-5" />,
    };
    
    return icons[type as keyof typeof icons] || icons.other;
  };
  
  const getActivityColor = (type: string) => {
    const colors = {
      note: 'bg-blue-100 text-blue-600',
      status_change: 'bg-purple-100 text-purple-600',
      task: 'bg-green-100 text-green-600',
      document: 'bg-amber-100 text-amber-600',
      call: 'bg-pink-100 text-pink-600',
      email: 'bg-indigo-100 text-indigo-600',
      meeting: 'bg-orange-100 text-orange-600',
      other: 'bg-gray-100 text-gray-600',
    };
    
    return colors[type as keyof typeof colors] || colors.other;
  };
  
  const sortActivities = (activities: Activity[]): Activity[] => {
    return [...activities].sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading activities...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            <p>{error}</p>
            <p className="mt-2">Please try refreshing the page.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CardHeader className="pb-0">
          <CollapsibleTrigger className="w-full text-left">
            <CardTitle className="flex justify-between items-center">
              Activity Timeline
              <span className="text-sm font-normal text-muted-foreground">
                {isOpen ? 'Hide' : 'Show'} ({activities.length})
              </span>
            </CardTitle>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-6">
            {activities.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <p>No activities recorded yet.</p>
              </div>
            ) : (
              <div className="relative space-y-8">
                <div className="absolute left-7 top-0 bottom-0 w-0.5 bg-muted"></div>
                
                {sortActivities(activities).map((activity) => (
                  <div key={activity.id} className="relative flex gap-4">
                    <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full ${getActivityColor(activity.type)}`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex flex-col flex-1">
                      <div className="flex items-baseline flex-wrap gap-2">
                        <span className="font-medium">{activity.title}</span>
                        {activity.type === 'email' && activity.metadata?.status && (
                          <Badge 
                            variant={
                              activity.metadata.status === 'delivered' ? 'default' : 
                              activity.metadata.status === 'sent' ? 'secondary' : 
                              'destructive'
                            }
                            className="text-xs"
                          >
                            {activity.metadata.status}
                          </Badge>
                        )}
                        <span className="ml-auto text-xs text-muted-foreground">
                          {format(
                            activity.createdAt instanceof Date 
                              ? activity.createdAt 
                              : new Date(activity.createdAt), 
                            'MMM d, yyyy h:mm a'
                          )}
                        </span>
                      </div>
                      {activity.description && (
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">
                          {activity.description}
                        </p>
                      )}
                      {activity.performedBy && (
                        <p className="text-xs text-muted-foreground mt-1">
                          By: {activity.performedBy}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default ActivityTimeline;
